<script setup lang="ts">
import { ref, type PropType } from 'vue';
import GaiaAddButton from '../base/GaiaAddButton.vue';
import GaiaButton from '../base/GaiaButton.vue';
import GaiaDeleteButton from '../base/GaiaDeleteButton.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaGlyphPaths } from '../glyphs';
import { gaiaFonts, gaiaFontSize, gaiaFontWeight, gaiaNeutral } from '../themes/tokens';
import GaiaCheckbox from '../base/GaiaCheckbox.vue';

export interface GaiaBoardColumnsMenuItem {
  key: string;
  label: string;
  /** Mono count on the right, e.g. cards in the column. */
  count?: string | number;
  visible: boolean;
  /** Show the delete action (e.g. only for empty custom columns). */
  canDelete?: boolean;
}

const props = defineProps({
  items: {
    type: Array as PropType<GaiaBoardColumnsMenuItem[]>,
    required: true,
  },
  /** Archived columns, restorable. */
  archived: {
    type: Array as PropType<{ key: string; label: string }[]>,
    default: () => [],
  },
  /** Offer the inline "Add column" affordance. */
  canAdd: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits<{
  toggle: [key: string];
  archive: [key: string];
  delete: [key: string];
  restore: [key: string];
  add: [name: string];
}>();

const adding = ref(false);
const newName = ref('');

function confirmAdd(): void {
  const name = newName.value.trim();
  if (!name) return;
  emit('add', name);
  newName.value = '';
  adding.value = false;
}

const faint = gaiaNeutral.textFaint;
const ink = gaiaNeutral.ink;
const text = gaiaNeutral.text;
const divider = gaiaNeutral.borderSubtle;
const hover = gaiaNeutral.surfaceHover;
const hoverStrong = gaiaNeutral.surfaceHoverStrong;
const inputBorder = gaiaNeutral.border;
const surface = gaiaNeutral.surface;
const monoFont = gaiaFonts.mono;
// The add-input is a native <input> and the add-trigger a <button>, so their
// type comes through v-bind rather than GaiaText props.
const rowSize = gaiaFontSize.dense;
const semibold = String(gaiaFontWeight.semibold);
</script>

<template>
  <!-- Board columns manager (Reviews Kanban mock): visibility checkboxes with
       counts, archive/delete per column, archived section with restore, and
       inline add-column. Presentational panel — host it in an n-popover. -->
  <n-flex vertical :size="1" :style="{ width: '268px' }">
    <gaia-text tag="div" class="section-label" variant="label" size="nano" tone="faint">
      Visible columns
    </gaia-text>
    <n-flex
      v-for="item in props.items"
      :key="item.key"
      align="center"
      :size="9"
      :wrap="false"
      class="row"
    >
      <button type="button" class="main" @click="emit('toggle', item.key)">
        <gaia-checkbox :checked="item.visible" tabindex="-1" style="pointer-events: none" />
        <gaia-text truncate size="dense" tone="text" :style="{ flex: 1, minWidth: 0 }">
          {{ item.label }}
        </gaia-text>
      </button>
      <gaia-text
        v-if="item.count !== undefined"
        size="micro"
        tone="muted"
        :style="{ flex: 'none', fontFamily: monoFont }"
      >
        {{ item.count }}
      </gaia-text>
      <button
        type="button"
        class="icon-action"
        title="Archive column"
        @click="emit('archive', item.key)"
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M4 7h16v3H4z" />
          <path d="M5.5 10v9h13v-9M10 14h4" />
        </svg>
      </button>
      <gaia-delete-button
        v-if="item.canDelete"
        size="xs"
        subject="column"
        @click="emit('delete', item.key)"
      />
    </n-flex>

    <template v-if="props.archived.length">
      <gaia-text tag="div" class="section-label divided" variant="label" size="nano" tone="faint">
        Archived
      </gaia-text>
      <n-flex
        v-for="item in props.archived"
        :key="item.key"
        align="center"
        :size="9"
        :wrap="false"
        class="row"
      >
        <gaia-text truncate size="dense" tone="faint" :style="{ flex: 1, minWidth: 0 }">
          {{ item.label }}
        </gaia-text>
        <gaia-button size="tiny" :style="{ marginLeft: 'auto' }" @click="emit('restore', item.key)">
          Restore
        </gaia-button>
      </n-flex>
    </template>

    <template v-if="props.canAdd">
      <n-flex
        v-if="adding"
        align="center"
        :size="7"
        :wrap="false"
        class="divided"
        :style="{ padding: '8px 9px 4px' }"
      >
        <input
          v-model="newName"
          class="add-input"
          placeholder="Column name"
          aria-label="Column name"
          @keydown.enter="confirmAdd"
        />
        <gaia-add-button subject="column" variant="primary" :icon="false" @click="confirmAdd" />
      </n-flex>
      <!-- A full-width menu row rather than a button, so it stays raw markup —
           but it draws the shared plus. -->
      <button v-else type="button" class="add-trigger divided" @click="adding = true">
        <svg
          width="13"
          height="13"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path :d="gaiaGlyphPaths.plus" />
        </svg>
        Add column
      </button>
    </template>
  </n-flex>
</template>

<style scoped>
.section-label {
  padding: 7px 9px 5px;
}

.divided {
  margin-top: 4px;
  border-top: 1px solid v-bind(divider);
}

.section-label.divided {
  padding-top: 8px;
}

.row {
  height: 34px;
  padding: 0 9px;
  border-radius: 8px;
}

.row:hover {
  background: v-bind(hover);
}

.main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 9px;
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
}

.icon-action {
  width: 24px;
  height: 24px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 7px;
  background: none;
  color: v-bind(faint);
  cursor: pointer;
}

.icon-action:hover {
  background: v-bind(hoverStrong);
  color: v-bind(ink);
}

.add-input {
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  height: 30px;
  padding: 0 10px;
  border: 1px solid v-bind(inputBorder);
  border-radius: 8px;
  outline: none;
  font-family: inherit;
  font-size: v-bind(rowSize);
  color: v-bind(ink);
  background: v-bind(surface);
}

.add-input:focus {
  border-color: v-bind(ink);
}

.add-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 9px;
  border: none;
  border-radius: 8px;
  background: none;
  font-family: inherit;
  font-size: v-bind(rowSize);
  font-weight: v-bind(semibold);
  color: v-bind(text);
  cursor: pointer;
  text-align: left;
}

.add-trigger:hover {
  background: v-bind(hover);
  color: v-bind(ink);
}
</style>
